import React, { useEffect, useState } from 'react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { User, Mail, Shield, Briefcase, Building2, Clock, Calendar, KeyRound } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import { validatePasswordComplexity } from '../utils/validation';
import { formatRoleName } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';
import { canReadEmployeeList } from '../utils/employeeListAccess';
import EmployeeDocuments from '../components/documents/EmployeeDocuments';

const Profile = () => {
  const { user, hasPermission } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  const [personalEmail, setPersonalEmail] = useState('');
  const [savingPersonalEmail, setSavingPersonalEmail] = useState(false);

  useEffect(() => {
    // Only the latest run may populate the form: StrictMode runs this effect twice in
    // dev, and a late response would overwrite the personal email the user has typed.
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/auth/profile');
        if (cancelled) return;
        setProfile(res.data.data);
        setPersonalEmail(res.data.data?.personal_email || '');
      } catch (err) {
        if (!cancelled) toast.error('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSavePersonalEmail = async (e) => {
    e.preventDefault();
    setSavingPersonalEmail(true);
    try {
      const res = await api.put('/api/auth/profile', { personal_email: personalEmail || null });
      setProfile(p => ({ ...p, personal_email: res.data.data?.personal_email ?? null }));
      toast.success('Personal email updated');
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.[0]?.msg
        || 'Failed to update personal email';
      toast.error(msg);
    } finally {
      setSavingPersonalEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    const complexity = validatePasswordComplexity(newPassword);
    if (!complexity.valid) {
      toast.error(complexity.error);
      return;
    }
    setChanging(true);
    try {
      await api.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const fullName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.username
    : '';
  const roleNames = (profile?.roles || []).map(r => formatRoleName(r.name)).filter(Boolean);
  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('') || (profile?.username?.[0]?.toUpperCase() ?? '?');

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start space-x-3 py-2">
      <Icon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 break-words">{value || '-'}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-soft p-6 flex items-center space-x-4">
        <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-semibold">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 truncate">{fullName}</h1>
          <p className="text-sm text-gray-500 truncate">{profile?.position || 'No position set'}</p>
          {roleNames.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {roleNames.map(name => (
                <span
                  key={name}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-soft">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
        </div>
        <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          <InfoRow icon={User} label="Username" value={profile?.username} />
          <InfoRow icon={Mail} label="Work email" value={profile?.email} />
          <InfoRow icon={Mail} label="Personal email" value={profile?.personal_email || 'Not set'} />
          <InfoRow icon={Briefcase} label="Position" value={profile?.position} />
          <InfoRow icon={Building2} label="Department" value={profile?.department_name} />
          <InfoRow icon={Shield} label="Roles" value={roleNames.join(', ')} />
          <InfoRow
            icon={Clock}
            label="Last Login"
            value={profile?.last_login ? new Date(profile.last_login).toLocaleString() : 'Never'}
          />
          <InfoRow
            icon={Calendar}
            label="Member Since"
            value={profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : null}
          />
        </div>
      </div>

      {/* The caller's own documents folder, when they have an employee record. */}
      {user?.employee_id != null && canReadEmployeeList(hasPermission, user, 'documents', { id: user.employee_id }) && (
        <EmployeeDocuments employeeId={user.employee_id} employeeName={fullName} className="border border-gray-200" />
      )}

      {/* Personal Email */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-soft">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center space-x-2">
          <Mail className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Personal Email</h2>
        </div>
        <form onSubmit={handleSavePersonalEmail} className="px-6 py-4 space-y-3">
          <p className="text-xs text-gray-500">
            Your personal email is used for account-recovery related emails and as a fallback when you can't access your work inbox.
            To change your work email, contact HR.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal email</label>
            <input
              type="email"
              data-testid="profile-field-personal-email"
              value={personalEmail}
              onChange={e => setPersonalEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="profile-save"
              disabled={savingPersonalEmail || personalEmail === (profile?.personal_email || '')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingPersonalEmail ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-soft">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center space-x-2">
          <KeyRound className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <PasswordInput
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Current password"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <PasswordInput
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="New password"
              />
              <p className="mt-1 text-xs text-gray-500">
                At least 8 characters, including one number and one symbol.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={changing}
              className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {changing ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile; 